import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Plus, X, Pencil, Trash, FunnelSimple, ListBullets, DotsSixVertical } from '@phosphor-icons/react';

import { apiPath } from '../config/apiBase';
import { formatUtcToIstDate } from '../modules/tickets/utils/datetime';
import { ModuleShell, SkeletonBlock } from './module-ui';
const API = apiPath('/api');

const EMPTY_FORM = {
  category: '',
  key: '',
  label: '',
  parent_key: '',
  order: 0,
  active: true,
};

function AdminLookupsPage() {
  const { user } = useAuth();
  const canManage = (user?.allowed_modules || []).includes('admin_lookups')
    || ['super_admin', 'admin'].includes(user?.role);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [lookups, setLookups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [showInactive, setShowInactive] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [reordering, setReordering] = useState(false);

  const config = useMemo(() => {
    const token = localStorage.getItem('token');
    return {
      withCredentials: true,
      headers: { Authorization: `Bearer ${token}` },
    };
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/lookups/categories`, config);
      setCategories(res.data);
      if (!selectedCategory && res.data.length) {
        setSelectedCategory(res.data[0]);
      }
    } catch (e) {
      console.error('fetchCategories', e);
    }
  };

  const fetchLookups = async () => {
    if (!selectedCategory) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lookups`, {
        ...config,
        params: { category: selectedCategory, include_inactive: showInactive },
      });
      setLookups(res.data);
    } catch (e) {
      console.error('fetchLookups', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, showInactive]);

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...EMPTY_FORM, category: selectedCategory, order: lookups.length });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormData({
      category: row.category,
      key: row.key,
      label: row.label,
      parent_key: row.parent_key || '',
      order: row.order || 0,
      active: row.active,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManage) return;
    try {
      if (editing) {
        const payload = {
          label: formData.label,
          parent_key: formData.parent_key || null,
          order: Number(formData.order) || 0,
          active: !!formData.active,
        };
        await axios.patch(`${API}/lookups/${editing.lookup_id}`, payload, config);
      } else {
        const payload = {
          category: formData.category.trim(),
          key: formData.key.trim(),
          label: formData.label.trim(),
          parent_key: formData.parent_key.trim() || null,
          order: Number(formData.order) || 0,
          active: !!formData.active,
        };
        await axios.post(`${API}/lookups`, payload, config);
        if (!categories.includes(payload.category)) {
          await fetchCategories();
          setSelectedCategory(payload.category);
        }
      }
      setShowModal(false);
      fetchLookups();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save lookup';
      alert(msg);
    }
  };

  const handleDelete = async (row) => {
    if (!canManage) return;
    if (!window.confirm(`Delete lookup "${row.label}" (${row.key})?`)) return;
    try {
      await axios.delete(`${API}/lookups/${row.lookup_id}`, config);
      fetchLookups();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete lookup');
    }
  };

  const persistOrder = async (ordered) => {
    setReordering(true);
    try {
      await axios.post(
        `${API}/lookups/reorder`,
        { items: ordered.map((r, idx) => ({ lookup_id: r.lookup_id, order: idx })) },
        config,
      );
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save new order');
      fetchLookups();
    } finally {
      setReordering(false);
    }
  };

  const onDragStart = (index) => (e) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(index)); } catch (_) { /* ignore */ }
  };

  const onDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropIndex !== index) setDropIndex(index);
  };

  const onDrop = (index) => async (e) => {
    e.preventDefault();
    const from = dragIndex;
    setDragIndex(null);
    setDropIndex(null);
    if (from == null || from === index) return;
    const next = [...lookups];
    const [moved] = next.splice(from, 1);
    next.splice(index, 0, moved);
    const ordered = next.map((r, idx) => ({ ...r, order: idx }));
    setLookups(ordered);
    await persistOrder(ordered);
  };

  const onDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  if (!canManage) {
    return (
      <div data-testid="lookups-access-denied" style={{ padding: '12px' }}>
        <h1 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '8px' }}>Access Denied</h1>
        <p style={{ color: '#6B7280' }}>You need the Manage Dropdowns module to edit dropdown values.</p>
      </div>
    );
  }

  return (
    <ModuleShell
      title="Manage Dropdowns"
      subtitle="Add, rename, drag to reorder, or disable any dropdown value used across the app"
      headerActions={(
        <button
          onClick={openCreate}
          className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          data-testid="create-lookup-button"
        >
          <Plus size={18} weight="bold" />
          Add Value
        </button>
      )}
    >
    <div data-testid="admin-lookups-page">

      <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '10px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <FunnelSimple size={20} weight="bold" />
          <span style={{ fontSize: '12px', fontWeight: '600' }}>Filter</span>
          {reordering && <span style={{ fontSize: '12px', color: '#6B7280' }}>Saving order…</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="input-field"
              style={{ padding: '6px 8px', fontSize: '12px' }}
              data-testid="lookup-category-filter"
            >
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer' }}
              data-testid="lookup-show-inactive-toggle"
            >
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show inactive rows
            </label>
          </div>
        </div>
      </div>

      {loading ? (
        <SkeletonBlock />
      ) : lookups.length === 0 ? (
        <div style={{ padding: '12px', background: 'white', border: '1px solid #E5E7EB', color: '#6B7280', fontSize: '12px' }} data-testid="lookups-empty">
          <ListBullets size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          No values yet in this category. Use "Add Value" to create one.
        </div>
      ) : (
        <div className="table-container">
          <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 8px' }}>
            Drag the handle on the left to rearrange. Order is saved automatically.
          </p>
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }} />
                <th>Order</th>
                <th>Key</th>
                <th>Label</th>
                <th>Parent Key</th>
                <th>Status</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lookups.map((row, index) => (
                <tr
                  key={row.lookup_id}
                  data-testid="lookup-row"
                  draggable
                  onDragStart={onDragStart(index)}
                  onDragOver={onDragOver(index)}
                  onDrop={onDrop(index)}
                  onDragEnd={onDragEnd}
                  style={{
                    opacity: dragIndex === index ? 0.45 : 1,
                    background: dropIndex === index && dragIndex !== index ? '#EFF6FF' : undefined,
                    cursor: 'grab',
                  }}
                >
                  <td style={{ color: '#9CA3AF', verticalAlign: 'middle' }} title="Drag to reorder">
                    <DotsSixVertical size={18} weight="bold" />
                  </td>
                  <td>{index}</td>
                  <td style={{ fontFamily: 'IBM Plex Mono, monospace', fontWeight: '600' }}>{row.key}</td>
                  <td>{row.label}</td>
                  <td style={{ color: '#6B7280', fontFamily: 'IBM Plex Mono, monospace', fontSize: '12px' }}>
                    {row.parent_key || '—'}
                  </td>
                  <td>
                    <span className={`badge ${row.active ? 'badge-low' : 'badge-closed'}`}>
                      {row.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ color: '#6B7280', fontSize: '12px' }}>
                    {row.updated_at ? formatUtcToIstDate(row.updated_at) : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => openEdit(row)}
                        className="btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        data-testid="edit-lookup-button"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(row)}
                        className="btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '12px', color: '#B91C1C', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        data-testid="delete-lookup-button"
                      >
                        <Trash size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          data-testid="lookup-modal"
        >
          <div style={{ background: 'white', padding: '32px', width: '100%', maxWidth: '560px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '600' }}>
                {editing ? `Edit "${editing.label}"` : 'Add New Dropdown Value'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                data-testid="lookup-modal-close"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input-field"
                  required
                  disabled={!!editing}
                  list="lookup-category-suggestions"
                  data-testid="lookup-category-input"
                />
                <datalist id="lookup-category-suggestions">
                  {categories.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Key</label>
                  <input
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    className="input-field"
                    required
                    disabled={!!editing}
                    data-testid="lookup-key-input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Label</label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    className="input-field"
                    required
                    data-testid="lookup-label-input"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Parent Key (optional)</label>
                <input
                  type="text"
                  value={formData.parent_key}
                  onChange={(e) => setFormData({ ...formData, parent_key: e.target.value })}
                  className="input-field"
                  placeholder="for cascading dropdowns"
                  data-testid="lookup-parent-input"
                />
                <p style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px' }}>
                  Prefer dragging rows in the list to change order — the Order field is optional.
                </p>
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    data-testid="lookup-active-checkbox"
                  />
                  Active (visible in app dropdowns)
                </label>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} data-testid="lookup-submit-button">
                  {editing ? 'Save Changes' : 'Create Value'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                  data-testid="lookup-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

export default AdminLookupsPage;
